<script lang="ts">
	import Katex from './Katex.svelte';
	import uqnSplit from './functions/uqnSplit';
	import Complex from './functions/complexClass';
	import ComplexSpecial from './functions/complexSpecialClass';
	import signAssign from './functions/signAssign';

	export let uqn: number;
	export let qnNameCode = 0; // generate qn labels (a), (b), (c), etc

	let t: number;
	$: t = uqnToType(uqn);
	let realImag = 4;
	let qn: string;
	$: qn = uqnToQn(uqn,t);

	function uqnToType(u:number):number{
		if (u > 999999){
			return 5;	
		} else{
			return Number(uqn.toString()[0]);
		}
	}

	function uqnToQn(u:number,t:number):string{
		if (t==1){ // convert cartesian to exponential
			return type1Generator(u);
		} else if (t==2){ // convert exponential to cartesian
			return type2Generator(u);
		} else if (t==3 || t==4){ // complex special class;
			return type34Generator(u);
		} else if (t==5){ // multiply/divide/powers/conjugate
			return type5Generator(u);
		} else if (t==6) { // real/purely imaginary
			return type6Generator(u);
		} else{
			throw "type not valid";
		}
	}

	function type1Generator(u:number):string{
		let [_,a,b,sign] = uqnSplit(u);
		[a,b] = signAssign(sign,a,b);
		const z = new Complex(a,b);
		return z.toString(false);
	}

	function type2Generator(u:number):string{
		let [_, r,theta1,theta2,theta3] = uqnSplit(u);
		let theta = theta1 + theta2/10 + theta3/100;
		if (theta>3.14){
			theta = -(6.28-theta);
		}
		let z = new Complex(Number((r*Math.cos(theta)).toPrecision(3)),Number((r*Math.sin(theta)).toPrecision(3)));
		return z.toString();
	}

	function type34Generator(u:number):string{
		let r: number, n1:number, n2:number, type: number;
		[type, r, n1, n2] = uqnSplit(u);
		let n = n1*10 + n2;
		let z = new ComplexSpecial(n,r);
		if (type==3){
			return z.toString(false);
		} else{
			return z.toString();
		}
	}

	function type5Generator(u:number):string{
		let [power, n1, n2, n3, n4, n5, conj ] = uqnSplit(u);
		let n = n1*10000+n2*1000+n3*100+n4*10+n5;
		n1 = Math.floor(n/24/24);
		n2 = Math.floor((n-n1*24*24)/24);
		n3 = n%24;
		let r = 1;
		if (conj>4){
			conj = conj%5;
			r = 2;
		}
		let z1 = new ComplexSpecial(n1,r);
		let z2 = new ComplexSpecial(n2);
		let z3 = new ComplexSpecial(n3);
		let z: ComplexSpecial;
		switch(conj){
			case 0:
				z = z1.multiply(z2.conjugate()).divide(z3.pow(power));
				return z.toString(false);
			case 1:
				z = z1.multiply(z2.pow(power)).divide(z3.conjugate());
				return z.toString(false);
			case 2:
				z = (z1.multiply(z2.pow(power))).conjugate().divide(z3);
				return z.toString(false);
			case 3:
				z = (z1.pow(power).multiply(z2).divide(z3)).conjugate();
				return z.toString(false);
			case 4:
				z = (z1.conjugate().multiply(z2)).pow(power).divide(z3);
				return z.toString(false);			
		}
	}

	function type6Generator(u:number):string{
		let n1:number, n2:number, _;
		[_, n1, n2, realImag] = uqnSplit(u);
		let n = n1*10+n2;
		const ansArray0 = ["error","12","6","4","3","12","error","12","3","4","6","12"];
		const ansArray1 = ["error","24","12","8","6","24","error","24","3","8","12","24","error","24","12","8","3","24","error","24","6","8","12","24"];
		const ansArray2 = ["error","12","6","4","3","12","error","12","error","4","6","12","error","12","6","4","error","12","error","12","3","4","6","12"];
		const ansArray3 = ["error","6","3","2","error","6","error","6","error","2","3","6","error","6","3","2","error","6","error","6","error","2","3","6"];
		switch (realImag){
			case 0:
				return ansArray0[n%12];
			case 1:
				return ansArray1[n];
			case 2:
				return ansArray2[n];
			case 3:
				return ansArray3[n];
		}
	}
</script>

<p> 
	({String.fromCharCode(qnNameCode+97)})
	<Katex math={qn}/>.
</p>